import { TourTip } from '@/components/TourTip';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Home as HomeIcon, Users2, Mail, UsersRound, Plus,
} from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { useMyHomes, useMyCommunities, useMyInvitations } from '@/hooks/useMe';
import { DASHBOARD_MODULES, ACCENT_STYLES, type ModuleDef } from '@/lib/modules';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export default function DashboardPage() {
  const { user } = useAuth();
  const { data: homesData, isLoading: homesLoading } = useMyHomes();
  const { data: commData, isLoading: commLoading } = useMyCommunities();
  const { data: inviteData } = useMyInvitations();

  const homes = homesData?.homes ?? [];
  const communities = commData?.communities ?? [];
  const invitations = inviteData?.invitations ?? [];
  const firstName = user?.displayName?.split(' ')[0] ?? 'friend';

  // Hero subtitle
  const roleSummary = [
    homes.length ? `${homes.length} Home${homes.length > 1 ? 's' : ''}` : null,
    communities.length ? `${communities.length} Communit${communities.length > 1 ? 'ies' : 'y'}` : null,
    invitations.length ? `${invitations.length} pending invite${invitations.length > 1 ? 's' : ''}` : null,
  ].filter(Boolean).join(' • ');

  return (
    <div className="space-y-8 pb-16">
      
      <TourTip
        id="dashboard-v1"
        title={"This is your home"}
        body={"Your family's command center. Everything starts from here."}
        bullets={["Tap a module below to dive in", "Invite family from Settings → Members", "Check notifications anytime from the top bar"]}
        accent="blue"
      />
      {/* ============ HERO ============ */}
      <section className="space-y-1">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
          Welcome home, {firstName}
        </h1>
        {roleSummary && (
          <p className="text-sm text-muted-foreground">
            You lead {roleSummary}
          </p>
        )}
      </section>

      {/* ============ KEY STATS ============ */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={HomeIcon}
          label="Homes"
          value={homesLoading ? null : homes.length}
          sublabel={homes[0]?.home.name}
          accent="steel"
        />
        <StatCard
          icon={UsersRound}
          label="Members"
          value={1}
          sublabel="Just you for now"
          accent="emerald"
        />
        <StatCard
          icon={Users2}
          label="Communities"
          value={commLoading ? null : communities.length}
          sublabel={communities[0]?.community.name}
          accent="rose"
        />
        <StatCard
          icon={Mail}
          label="Invitations"
          value={invitations.length}
          sublabel={invitations.length ? 'Awaiting your reply' : 'None waiting'}
          accent="amber"
          href="/invitations"
        />
      </section>

      {/* ============ PENDING INVITES BANNER ============ */}
      {invitations.length > 0 && (
        <Link to="/invitations" className="block group">
          <Card className="border-amber-300/50 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 hover:shadow-md transition-all">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center text-lg flex-shrink-0">
                ✉️
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">
                  You have {invitations.length} pending invitation{invitations.length > 1 ? 's' : ''}
                </p>
                <p className="text-xs text-muted-foreground">Tap to accept or decline</p>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
            </CardContent>
          </Card>
        </Link>
      )}

      {/* ============ YOUR HOMES ============ */}
      <section>
        <SectionHeading title="Your Homes" count={homes.length} />
        {homesLoading ? (
          <Skeleton className="h-24 rounded-xl" />
        ) : homes.length === 0 ? (
          <EmptyHomes />
        ) : (
          <div className="space-y-3">
            {homes.map(({ home, role }) => (
              <Link key={home.id} to={`/homes/${home.id}`} className="block group">
                <Card className="overflow-hidden border-border hover:border-primary/40 hover:shadow-md transition-all">
                  <div className="flex">
                    <div className="w-1.5 bg-gradient-to-b from-primary to-primary/70" />
                    <CardContent className="flex-1 p-5">
                      <div className="flex items-start gap-4">
                        <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                          <HomeIcon className="w-6 h-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-lg text-foreground">{home.name}</h3>
                            <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                              {role}
                            </Badge>
                          </div>
                          {home.motto && (
                            <p className="text-sm text-muted-foreground italic mt-1">"{home.motto}"</p>
                          )}
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
                            {home.region && <span>📍 {home.region}</span>}
                            <span>💰 {home.currencyCode}</span>
                            <span>🌍 {home.country}</span>
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0 mt-2" />
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ============ MODULES ============ */}
      <section>
        <SectionHeading title="Modules" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {DASHBOARD_MODULES.map((m) => (
            <ModuleCard
              key={m.key}
              module={m}
              extraInfo={
                m.key === 'community' && communities.length > 0
                  ? `${communities.length} joined`
                  : undefined
              }
            />
          ))}
        </div>
      </section>

      {/* ============ YOUR COMMUNITIES ============ */}
      {!commLoading && communities.length > 0 && (
        <section>
          <SectionHeading title="Your Communities" count={communities.length} />
          <div className="space-y-3">
            {communities.map(({ community, role }) => (
              <Card key={community.id} className="border-border">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-xl bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 flex items-center justify-center flex-shrink-0">
                      <Users2 className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground">{community.name}</h3>
                        <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                          {role}
                        </Badge>
                      </div>
                      {community.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {community.description}
                        </p>
                      )}
                      {community.joinCode && (
                        <div className="flex items-center gap-2 mt-3">
                          <span className="text-[11px] text-muted-foreground">Join code:</span>
                          <code className="text-xs font-mono bg-muted px-2 py-0.5 rounded font-semibold">
                            {community.joinCode}
                          </code>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* ============ QUICK ACTIONS ============ */}
      <section>
        <SectionHeading title="Quick Actions" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Button asChild variant="outline" className="h-auto py-4 justify-start">
            <Link to="/homes/new">
              <Plus className="w-4 h-4 mr-2" />
              <div className="text-left">
                <div className="font-medium">New Home</div>
                <div className="text-[11px] text-muted-foreground">Create another household</div>
              </div>
            </Link>
          </Button>
          <Button asChild variant="outline" className="h-auto py-4 justify-start">
            <Link to="/community">
              <Plus className="w-4 h-4 mr-2" />
              <div className="text-left">
                <div className="font-medium">Join Community</div>
                <div className="text-[11px] text-muted-foreground">Enter a join code</div>
              </div>
            </Link>
          </Button>
          <Button asChild variant="outline" className="h-auto py-4 justify-start">
            <Link to="/invitations">
              <Mail className="w-4 h-4 mr-2" />
              <div className="text-left">
                <div className="font-medium">Invitations</div>
                <div className="text-[11px] text-muted-foreground">
                  {invitations.length ? `${invitations.length} pending` : 'Nothing new'}
                </div>
              </div>
            </Link>
          </Button>
        </div>
      </section>

      {/* Footer spacer */}
      <div className="pt-6 text-center text-[11px] text-muted-foreground">
        KEJJA is being built alongside your family's story. 🏠
      </div>
    </div>
  );
}

/* ============ helpers ============ */

function SectionHeading({ title, count }: { title: string; count?: number }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {title}
      </h2>
      {count !== undefined && (
        <span className="text-[11px] text-muted-foreground">
          {count} total
        </span>
      )}
    </div>
  );
}

function StatCard({
  icon: Icon, label, value, sublabel, accent, href,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | null;
  sublabel?: string;
  accent: keyof typeof ACCENT_STYLES;
  href?: string;
}) {
  const styles = ACCENT_STYLES[accent];
  const content = (
    <Card className={cn('h-full transition-all', href && 'hover:shadow-md hover:border-primary/40')}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0', styles.iconBg, styles.iconText)}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-2xl font-bold text-foreground leading-none">
              {value === null ? <Skeleton className="h-7 w-10" /> : value}
            </div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1 font-semibold">
              {label}
            </div>
          </div>
        </div>
        {sublabel && (
          <p className="text-[11px] text-muted-foreground mt-2 line-clamp-1">
            {sublabel}
          </p>
        )}
      </CardContent>
    </Card>
  );

  return href ? <Link to={href} className="block">{content}</Link> : content;
}

function ModuleCard({ module: m, extraInfo }: { module: ModuleDef; extraInfo?: string }) {
  const styles = ACCENT_STYLES[m.accent];
  return (
    <Link to={m.route} className="group">
      <Card className={cn('h-full overflow-hidden transition-all border-border', styles.border, 'hover:shadow-md')}>
        <div className={cn('h-1', styles.strip)} />
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className={cn('h-11 w-11 rounded-xl flex items-center justify-center', styles.iconBg, styles.iconText)}>
              <m.icon className="w-5 h-5" />
            </div>
            {m.status === 'coming_soon' && (
              <Badge variant="outline" className="text-[10px] uppercase tracking-wide">Soon</Badge>
            )}
          </div>
          <div className="mt-3">
            <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
              {m.label}
            </h3>
            {m.tagline && (
              <p className="text-xs font-medium italic text-muted-foreground/90 mt-0.5">
                {m.tagline}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed line-clamp-2">
              {m.description}
            </p>
            {extraInfo && (
              <p className={cn('text-xs font-semibold mt-3', styles.iconText)}>
                {extraInfo}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function EmptyHomes() {
  return (
    <Card className="border-dashed">
      <CardContent className="p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-primary/10 text-primary mx-auto flex items-center justify-center mb-3">
          <HomeIcon className="w-5 h-5" />
        </div>
        <h3 className="font-semibold text-foreground">Create your first Home</h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-xs mx-auto">
          A Home is where your family's finances, routines, and memories live.
        </p>
        <Button asChild size="sm" className="mt-4">
          <Link to="/homes/new">
            <Plus className="w-4 h-4 mr-1.5" />
            Create Home
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
